import React, { useEffect, useState } from "react";
import { Scenario, Persona, RevealedDocument, SessionTool } from "../types";
import { initialsOf } from "./PersonaAvatar";
import MarkdownMessage from "./MarkdownMessage";
import { personaHue } from "./PersonaHue";
import FileDrawer from "./FileDrawer";
import CoachPanel from "./CoachPanel";
import AnswerPanel from "./AnswerPanel";

interface SessionToolColumnProps {
  activeTool: SessionTool;
  scenario: Scenario;
  activePersona: Persona;
  sessionId: string;
  activePersonaId: string;
  getToken: () => string | undefined;
  scratchpadText: string;
  onScratchpadChange: (v: string) => void;
  answerText: string;
  onAnswerChange: (v: string) => void;
  revealedDocuments: RevealedDocument[];
  documentsOpen: boolean;
  onDocumentsClose: () => void;
  onSelectTool: (tool: SessionTool) => void;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

/**
 * The portrait block for a persona with no usable image — either none was set,
 * or the signed URL failed to load. Both paths render this, so there is one
 * appearance rather than two.
 */
const PersonaInitials: React.FC<{ name: string; hue: string }> = ({ name, hue }) => (
  <div
    className="grid place-items-center w-full h-[252px] font-headline font-bold text-[70px]"
    style={{
      color: hue,
      // The hue is a runtime value, so this tint has to be an inline color-mix.
      // The theme tokens are declared in Tailwind's JS config (web/index.html),
      // not as CSS custom properties, so there is no var() to mix against: this
      // literal mirrors `surface-container-high`. Change both together.
      background: `color-mix(in srgb, ${hue} 13%, #222a3d)`,
    }}
  >
    {initialsOf(name)}
  </div>
);

const ScenarioView: React.FC<{ scenario: Scenario }> = ({ scenario }) => {
  // Scenario art is a short-lived signed GCS URL, so a page left open long
  // enough will see it expire. Fall through to the gradient-and-grid band
  // rather than leaving a bare strip where the art was.
  const [heroFailed, setHeroFailed] = useState(false);
  useEffect(() => {
    setHeroFailed(false);
  }, [scenario.hero]);
  const showHero = !!scenario.hero && !heroFailed;

  // The logo is a separate signed URL and expires the same way. It falls back
  // to no mark at all, which is exactly how this band already renders a
  // scenario that never had a logo — one absence, one appearance.
  const [logoFailed, setLogoFailed] = useState(false);
  useEffect(() => {
    setLogoFailed(false);
  }, [scenario.logo]);
  const showLogo = !!scenario.logo && !logoFailed;

  return (
    <div className="overflow-y-auto h-full pt-3">
      {/* Hero band. 156px is short enough that the no-art fallback reads as a
          header rather than a void — which is the state both seeded scenarios
          ship in. No category label here: the player already chose this
          scenario, so the badge is noise mid-session. */}
      <div className="relative isolate flex items-end h-[156px] border-y border-outline-variant/35">
        {showHero ? (
          <>
            <img
              src={scenario.hero}
              alt=""
              onError={() => setHeroFailed(true)}
              className="absolute inset-0 w-full h-full object-cover -z-30"
            />
            <div className="absolute inset-0 -z-20 bg-surface-container-lowest opacity-35 mix-blend-multiply" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-surface-container via-surface-container/70 to-transparent" />
          </>
        ) : (
          <>
            <div className="absolute inset-0 -z-30 bg-surface-container-low" />
            <div className="absolute inset-0 -z-20 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent" />
            <div
              className="absolute inset-0 -z-10 opacity-5 text-on-surface"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(0deg,transparent,transparent 23px,currentColor 23px,currentColor 24px),repeating-linear-gradient(90deg,transparent,transparent 23px,currentColor 23px,currentColor 24px)",
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-surface-container to-transparent" />
          </>
        )}
        <div className="relative flex items-end gap-3 w-full px-[18px] pb-3.5">
          {showLogo && (
            <img
              src={scenario.logo}
              alt=""
              onError={() => setLogoFailed(true)}
              className="w-[38px] h-[38px] shrink-0 rounded-md object-cover border border-on-surface/20"
            />
          )}
          <h4 className="font-headline font-bold text-[17px] leading-tight text-white m-0 text-balance">
            {scenario.title}
          </h4>
        </div>
      </div>

      <div className="px-5 pt-4 pb-7">
        {scenario.playerRole && (
          <>
            <p className="text-[10px] font-label font-semibold text-slate-500 tracking-[0.17em] uppercase mb-1.5">
              Your role
            </p>
            <div className="text-[13.5px] leading-snug text-on-surface mb-4">
              <MarkdownMessage text={scenario.playerRole} variant="compact" />
            </div>
          </>
        )}
        {scenario.objective && (
          <>
            <p className="text-[10px] font-label font-semibold text-slate-500 tracking-[0.17em] uppercase mb-1.5">
              Your objective
            </p>
            <div className="text-[13.5px] leading-snug text-on-surface mb-4">
              <MarkdownMessage text={scenario.objective} variant="compact" />
            </div>
          </>
        )}

        <div className="flex items-center gap-4 mt-5 mb-3">
          <span className="text-[10.5px] font-label font-bold text-primary tracking-[0.19em] uppercase whitespace-nowrap">
            Scenario
          </span>
          <span className="flex-1 h-px bg-primary/25" />
        </div>
        {/* Falls back to `description` so the pane is never empty on a scenario
            authored before `briefing` existed. */}
        <div className="text-[13px] text-slate-400">
          <MarkdownMessage
            text={scenario.briefing || scenario.description}
            variant="compact"
          />
        </div>
      </div>
    </div>
  );
};

const PersonaView: React.FC<{ persona: Persona; hue: string }> = ({ persona, hue }) => {
  // Same story as the scenario hero: the avatar is a short-lived signed URL and
  // a chat session easily outlives one, so a failed load is ordinary wear. It
  // falls back to the initials block instead of a broken-image box.
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => {
    setAvatarFailed(false);
  }, [persona.avatar]);
  const showAvatar = !!persona.avatar && !avatarFailed;

  return (
    <div className="overflow-y-auto h-full pt-3">
      {/* Rounded, bordered, inset from the pane edges — the card is what keeps
          this from reading as a hard-edged slab. Border takes the persona's
          hue at low alpha. */}
      <div
        className="mx-3.5 mb-5 rounded-[9px] overflow-hidden bg-surface-container-low border"
        style={{ borderColor: `color-mix(in srgb, ${hue} 34%, transparent)` }}
      >
        {/* Portrait at full card width. Name and role sit ON the image behind a
            scrim, so identity costs no extra height — and it mirrors the
            Scenario pane directly above it in the same column. */}
        <div className="relative isolate">
          {showAvatar ? (
            <img
              src={persona.avatar}
              alt=""
              onError={() => setAvatarFailed(true)}
              className="block w-full h-[252px] object-cover bg-surface-container-high"
            />
          ) : (
            <PersonaInitials name={persona.name} hue={hue} />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-surface-container-low via-surface-container-low/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 px-4 pb-3.5">
            <p className="font-headline font-bold text-[19px] leading-tight text-white m-0 mb-1 text-balance">
              {persona.name}
            </p>
            <p
              className="text-[9.5px] font-label font-semibold tracking-[0.14em] uppercase m-0"
              style={{ color: hue }}
            >
              {persona.role}
            </p>
          </div>
        </div>

        {(persona.description || persona.bio) && (
          <div className="px-4 pt-3.5 pb-4">
            {persona.description && (
              <p className="text-[13px] leading-relaxed text-slate-300 m-0">
                {persona.description}
              </p>
            )}
            {persona.description && persona.bio && (
              <div className="h-px bg-outline-variant/50 my-3.5" />
            )}
            {persona.bio && (
              <div className="text-[12.5px] text-slate-400">
                <MarkdownMessage text={persona.bio} variant="compact" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ScratchpadView: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <div className="flex flex-col p-6 space-y-4 h-full">
    <div className="flex items-center gap-2">
      <span className="material-symbols-outlined text-tertiary text-sm">edit_note</span>
      <h3 className="text-xs font-label font-bold text-tertiary tracking-[0.2em] uppercase">Scratchpad</h3>
    </div>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Jot down notes, observations, and hypotheses..."
      className="flex-1 w-full bg-surface-container-low border border-outline-variant/20 rounded-lg p-3 text-sm text-on-surface font-body placeholder-slate-500 resize-none focus:outline-none focus:border-tertiary/50 focus:ring-1 focus:ring-tertiary/30"
    />
  </div>
);

const ToolContent: React.FC<SessionToolColumnProps> = (props) => {
  switch (props.activeTool) {
    case SessionTool.SCENARIO:
      return <ScenarioView scenario={props.scenario} />;
    case SessionTool.PERSONA:
      return (
        <PersonaView
          persona={props.activePersona}
          hue={personaHue(props.scenario.personas, props.activePersona.id)}
        />
      );
    case SessionTool.SCRATCHPAD:
      return <ScratchpadView value={props.scratchpadText} onChange={props.onScratchpadChange} />;
    case SessionTool.ANSWER:
      return (
        <AnswerPanel
          sessionId={props.sessionId}
          value={props.answerText}
          onChange={props.onAnswerChange}
          getToken={props.getToken}
        />
      );
    case SessionTool.COACH:
      return (
        <CoachPanel
          sessionId={props.sessionId}
          activePersonaId={props.activePersonaId}
          scratchpad={props.scratchpadText}
          getToken={props.getToken}
        />
      );
    default:
      return null;
  }
};

const TOOL_TABS: { tool: SessionTool; icon: string; label: string }[] = [
  { tool: SessionTool.SCENARIO, icon: "description", label: "Scenario" },
  { tool: SessionTool.PERSONA, icon: "person", label: "Persona" },
  { tool: SessionTool.SCRATCHPAD, icon: "edit_note", label: "Scratchpad" },
  { tool: SessionTool.ANSWER, icon: "assignment", label: "Submit work" },
  { tool: SessionTool.COACH, icon: "psychology", label: "Coach" },
];

const SessionToolColumn: React.FC<SessionToolColumnProps> = (props) => {
  const {
    activeTool,
    onSelectTool,
    collapsed,
    onCollapsedChange,
    documentsOpen,
    onDocumentsClose,
    scenario,
    revealedDocuments,
  } = props;

  return (
    <>
      {/* Documents overlay backdrop — every breakpoint */}
      <div
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          documentsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onDocumentsClose}
      />

      {/* One column at every width. Below xl it simply starts collapsed, which
          is what the old mobile drawer was for — so the duplicated rendering
          of ToolContent goes away rather than gaining a third case. */}
      <aside
        className={`relative shrink-0 flex flex-col items-end overflow-hidden bg-surface-container border-l border-outline-variant/20 transition-[width] duration-200 ease-out motion-reduce:transition-none ${
          collapsed ? "w-11" : "w-[340px]"
        }`}
      >
        <div
          className={`w-[340px] flex-1 min-h-0 flex flex-col transition-opacity duration-150 motion-reduce:transition-none ${
            collapsed ? "opacity-0 pointer-events-none" : "opacity-100"
          }`}
          inert={collapsed}
        >
          <div className="flex items-center gap-px px-2 pt-2.5 pb-2 border-b border-outline-variant/30">
            {TOOL_TABS.map(({ tool, icon, label }) => {
              const isActive = activeTool === tool;
              return (
                <button
                  key={tool}
                  type="button"
                  onClick={() => onSelectTool(tool)}
                  title={label}
                  aria-pressed={isActive}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors ${
                    isActive
                      ? "text-primary bg-primary/10"
                      : "text-slate-500 hover:text-on-surface hover:bg-surface-container-high"
                  }`}
                >
                  <span className="material-symbols-outlined text-[18px]">{icon}</span>
                  {/* The active tab carries the label the pane eyebrow used to
                      carry, so the row costs no height the eyebrow was not
                      already spending. */}
                  {isActive && (
                    <span className="font-label text-[10px] font-bold tracking-[0.16em] uppercase whitespace-nowrap">
                      {label}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => onCollapsedChange(true)}
              title="Collapse tools"
              className="ml-auto p-1.5 rounded-md text-slate-500 hover:text-on-surface hover:bg-surface-container-high transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">right_panel_close</span>
            </button>
          </div>

          <div className="flex-1 min-h-0">
            <ToolContent {...props} />
          </div>
        </div>

        <div
          className={`absolute inset-y-0 right-0 w-11 flex flex-col items-center gap-0.5 py-2.5 bg-surface-container border-l border-outline-variant/20 transition-opacity duration-150 delay-50 motion-reduce:transition-none ${
            collapsed ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          inert={!collapsed}
        >
          <button
            type="button"
            onClick={() => onCollapsedChange(false)}
            title="Expand tools"
            className="p-1.5 rounded text-slate-400 hover:text-on-surface hover:bg-surface-container-high transition-colors"
          >
            <span className="material-symbols-outlined text-[19px]">right_panel_open</span>
          </button>
          <span className="w-5 h-px bg-outline-variant/50 my-1" />
          {TOOL_TABS.map(({ tool, icon, label }) => (
            <button
              key={tool}
              type="button"
              onClick={() => {
                onSelectTool(tool);
                onCollapsedChange(false);
              }}
              title={label}
              className={`p-1.5 rounded transition-colors ${
                activeTool === tool
                  ? "text-primary bg-primary/10"
                  : "text-slate-400 hover:text-on-surface hover:bg-surface-container-high"
              }`}
            >
              <span className="material-symbols-outlined text-[19px]">{icon}</span>
            </button>
          ))}
        </div>
      </aside>

      {/* Documents overlay — unchanged behaviour, overlay at every breakpoint */}
      <aside
        className={`fixed inset-y-0 right-0 z-50 w-80 bg-surface-container-low flex flex-col shrink-0 border-l border-outline-variant/10 transform transition-transform duration-300 ease-in-out ${
          documentsOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-end p-2">
          <button
            onClick={onDocumentsClose}
            className="text-slate-500 hover:text-slate-300 transition-colors"
            title="Close"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>
        <FileDrawer
          documents={scenario.documents}
          revealedDocs={revealedDocuments}
          personas={scenario.personas}
        />
      </aside>
    </>
  );
};

export default SessionToolColumn;
